import { useState, type FormEvent } from 'react';
import {
  useDocuments,
  useUploadDocument,
  useUpdateDocument,
  type DocumentFilters,
} from '../hooks/useDocuments';
import { useJournalEntries } from '../hooks/useJournalEntries';
import type { Document } from '../types';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const [filters, setFilters] = useState<DocumentFilters>({});
  const [draft, setDraft] = useState<DocumentFilters>({});
  const { data: documents, isLoading, error } = useDocuments(filters);
  const { data: journalEntries } = useJournalEntries();
  const uploadMut = useUploadDocument();
  const updateMut = useUpdateDocument();

  const [showUpload, setShowUpload] = useState(false);
  const [linkingDocId, setLinkingDocId] = useState<string | null>(null);

  const onUpload = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const file = fd.get('file');
    if (!(file instanceof File) || file.size === 0) {
      alert('ファイルを選択してください');
      return;
    }
    const documentDate = String(fd.get('documentDate'));
    const amountStr = String(fd.get('amount'));
    const counterpartyName = String(fd.get('counterpartyName')).trim();

    try {
      await uploadMut.mutateAsync({
        file,
        documentDate,
        amount: amountStr ? Number(amountStr) : undefined,
        counterpartyName: counterpartyName || undefined,
      });
      setShowUpload(false);
      (e.target as HTMLFormElement).reset();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'アップロード失敗');
    }
  };

  const onLink = async (doc: Document, journalEntryId: string) => {
    try {
      await updateMut.mutateAsync({ id: doc.id, journalEntryId });
      setLinkingDocId(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : '紐付け失敗');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>証憑管理</h2>
        <button className="primary" onClick={() => setShowUpload(!showUpload)}>
          + アップロード
        </button>
      </div>

      {error && <div className="error-banner">{(error as Error).message}</div>}

      {showUpload && (
        <div className="card">
          <h3>証憑アップロード</h3>
          <p className="text-muted" style={{ fontSize: 13 }}>
            対応形式：PDF / JPEG / PNG（最大 20MB）。アップロード後の上書き・削除は法令により禁止されます。
          </p>
          <form onSubmit={onUpload}>
            <div className="form-row">
              <label>ファイル *</label>
              <input type="file" name="file" accept=".pdf,image/jpeg,image/png" required />
            </div>
            <div className="form-row">
              <label>取引日付 *</label>
              <input type="date" name="documentDate" required />
            </div>
            <div className="form-row">
              <label>金額</label>
              <input type="number" name="amount" min="0" step="1" />
            </div>
            <div className="form-row">
              <label>取引先</label>
              <input type="text" name="counterpartyName" maxLength={200} />
            </div>
            <div className="toolbar">
              <button type="submit" className="primary" disabled={uploadMut.isPending}>
                {uploadMut.isPending ? 'アップロード中...' : 'アップロード'}
              </button>
              <button type="button" onClick={() => setShowUpload(false)}>
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="toolbar">
          <label>
            日付From{' '}
            <input
              type="date"
              value={draft.dateFrom ?? ''}
              onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
            />
          </label>
          <label>
            日付To{' '}
            <input
              type="date"
              value={draft.dateTo ?? ''}
              onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
            />
          </label>
          <label>
            金額Min{' '}
            <input
              type="number"
              value={draft.amountMin ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, amountMin: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </label>
          <label>
            金額Max{' '}
            <input
              type="number"
              value={draft.amountMax ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, amountMax: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </label>
          <label>
            取引先{' '}
            <input
              value={draft.counterpartyName ?? ''}
              onChange={(e) => setDraft({ ...draft, counterpartyName: e.target.value || undefined })}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={draft.unlinked ?? false}
              onChange={(e) => setDraft({ ...draft, unlinked: e.target.checked || undefined })}
            />{' '}
            未紐付けのみ
          </label>
          <button className="primary" onClick={() => setFilters(draft)}>
            検索
          </button>
          <button
            onClick={() => {
              setDraft({});
              setFilters({});
            }}
          >
            リセット
          </button>
        </div>
      </div>

      {isLoading && <div>読み込み中...</div>}

      {documents && (
        <table>
          <thead>
            <tr>
              <th style={{ width: 110 }}>取引日付</th>
              <th>ファイル名</th>
              <th>取引先</th>
              <th className="numeric" style={{ width: 110 }}>金額</th>
              <th style={{ width: 90 }}>サイズ</th>
              <th style={{ width: 110 }}>保存期限</th>
              <th>仕訳紐付け</th>
              <th style={{ width: 110 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((d) => (
              <tr key={d.id}>
                <td>{d.documentDate}</td>
                <td>
                  <a href={`/api/documents/${d.id}/content`} target="_blank" rel="noreferrer">
                    {d.originalFilename}
                  </a>
                  <div className="text-muted" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                    SHA256: {d.sha256Hash.slice(0, 16)}...
                  </div>
                </td>
                <td>{d.counterpartyName ?? <span className="text-muted">—</span>}</td>
                <td className="numeric">
                  {d.amount !== null ? d.amount.toLocaleString() : <span className="text-muted">—</span>}
                </td>
                <td>{formatBytes(d.fileSize)}</td>
                <td className="text-muted" style={{ fontSize: 12 }}>
                  {d.expiresAt}
                </td>
                <td>
                  {linkingDocId === d.id ? (
                    <select
                      onChange={(e) => {
                        if (e.target.value) onLink(d, e.target.value);
                      }}
                      defaultValue=""
                    >
                      <option value="">仕訳を選択</option>
                      {journalEntries?.map((je) => (
                        <option key={je.id} value={je.id}>
                          {je.entryDate} {je.description}
                        </option>
                      ))}
                    </select>
                  ) : d.journalEntryId ? (
                    <span style={{ fontSize: 12 }}>
                      {journalEntries?.find((je) => je.id === d.journalEntryId)?.description ?? d.journalEntryId}
                    </span>
                  ) : (
                    <span className="badge draft">未紐付け</span>
                  )}
                </td>
                <td>
                  {linkingDocId === d.id ? (
                    <button onClick={() => setLinkingDocId(null)}>キャンセル</button>
                  ) : (
                    <button onClick={() => setLinkingDocId(d.id)}>紐付け</button>
                  )}
                </td>
              </tr>
            ))}
            {documents.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 24 }} className="text-muted">
                  証憑がありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
